import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";

export function CreateAssignmentModal({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [maxScore, setMaxScore] = useState(100);
  const [dueAt, setDueAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.post(`/api/v1.0/courses/${courseId}/assignments`, {
        title,
        instructions,
        max_score: maxScore,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "assignments"] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Failed to create assignment"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate();
  };

  return (
    <Modal title="Create assignment" onClose={onClose}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-slate-700">Instructions</label>
          <textarea
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            rows={4}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Max score" type="number" min={1} value={maxScore} onChange={(e) => setMaxScore(Number(e.target.value))} />
          <Input label="Due date" type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            Create assignment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
